import { CSSProperties } from 'react';
import { Collapse } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import clsx from 'clsx';

import { ChipGroupProps } from '../chipGroup/ChipGroup';

import { WidgetHeader } from './WidgetHeader';

import styles from './widget.module.scss';

export type WidgetAction = {
  title: string;
  icon: {
    name: string;
    solid?: boolean;
    svgIcon?: boolean;
    className?: string;
  };
  label?: string;
  cb?: () => void | Promise<void>;
  disabled?: boolean;
  /** Draws a thin divider after this action, before the following icons. */
  separatorAfter?: boolean;
  // opens a menu and shows more actions as menu items
  moreActions?: WidgetMoreAction[];
};

export type WidgetMoreAction = {
  icon?: { name: string; solid?: boolean; svgIcon?: boolean };
  label: string;
  tooltip?: string;
  cb?: () => void | Promise<void>;
  disabled?: boolean;
};

export type WidgetChipGroupProps = {
  data: ChipGroupProps['data'];
  defaultValue?: ChipGroupProps['defaultValue'];
  value?: ChipGroupProps['value'];
  onChange?: ChipGroupProps['onChange'];
};

export type WidgetClassNames = {
  root?: string;
  header?: string;
  title?: string;
};

export type WidgetProps = {
  actions?: WidgetAction[];
  children?: React.ReactNode;
  chipGroup?: WidgetChipGroupProps;
  classNames?: WidgetClassNames;
  collapsible?: boolean;
  dataTestId?: string;
  enableScrollElementToTop?: boolean;
  style?: CSSProperties;
  contentStyle?: CSSProperties;
  title: string;
};

export function Widget({
  children,
  dataTestId,
  style,
  contentStyle,
  ...widgetHeaderProps
}: WidgetProps) {
  const [opened, { toggle }] = useDisclosure(true);

  return (
    <div
      className={clsx(
        'widget-root',
        styles['root'],
        widgetHeaderProps.classNames?.root
      )}
      data-testid={dataTestId}
      style={style}
    >
      <WidgetHeader
        {...widgetHeaderProps}
        dataTestId={dataTestId != null ? `${dataTestId}-header` : undefined}
        titleDataTestId={dataTestId != null ? `${dataTestId}-title` : undefined}
        toggleContent={toggle}
        opened={opened}
      />
      {children && (
        <Collapse
          className={styles['collapse']}
          expanded={opened}
          style={contentStyle}
        >
          {children}
        </Collapse>
      )}
    </div>
  );
}
